import { useState } from "react";
import { invokeAdmin, errMsg } from "../lib/adminApi";
import { useAdminQuery } from "../hooks/useAdminQuery";
import { Banner, CopyId, PillTabs } from "../components/ui";

type EntityKind = "venue" | "organizer";
type EntityRoleName = "owner" | "editor" | "moderator";

type EntityRole = {
  id: string;
  user_id: string;
  entity_kind: EntityKind;
  entity_id: string;
  role: EntityRoleName;
  created_at: string;
};

type KindFilter = "all" | EntityKind;

const FILTERS: { key: KindFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "venue", label: "Venue" },
  { key: "organizer", label: "Organizer" },
];

export default function EntityRolesPage() {
  const [filter, setFilter] = useState<KindFilter>("all");
  const [revokeLoading, setRevokeLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Cancellation-safe fetch keyed on the kind filter (ADM-5).
  const { data, loading, error, refetch } = useAdminQuery(
    () => {
      const body: Record<string, unknown> = { limit: 50 };
      if (filter !== "all") body.entity_kind = filter;
      return invokeAdmin<{ roles: EntityRole[]; total?: number }>(
        "admin_entity_roles_list",
        body,
      );
    },
    [filter],
  );
  const roles = data?.roles ?? [];

  async function revokeRole(id: string) {
    if (!window.confirm("Revoke this role? This cannot be undone.")) return;
    setRevokeLoading(id);
    setActionError(null);
    try {
      await invokeAdmin("admin_entity_role_delete", { id });
      refetch();
    } catch (e) {
      setActionError(errMsg(e));
    } finally {
      setRevokeLoading(null);
    }
  }

  return (
    <div>
      <h1 className="text-xl font-semibold">Roles</h1>

      <PillTabs className="mt-4" tabs={FILTERS} active={filter} onChange={setFilter} />

      {error && <Banner className="mt-3">{error}</Banner>}
      {actionError && <Banner className="mt-3">{actionError}</Banner>}

      <div className="mt-4 rounded-lg border border-zinc-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 text-left text-xs uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="px-3 py-2 font-medium">Role</th>
              <th className="px-3 py-2 font-medium">Entity</th>
              <th className="px-3 py-2 font-medium">User</th>
              <th className="px-3 py-2 font-medium">Granted</th>
              <th className="px-3 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {roles.map((r) => (
              <tr key={r.id} className="border-t border-zinc-800 align-top">
                <td className="px-3 py-2">
                  <span className="rounded bg-zinc-800 px-2 py-0.5 text-xs text-zinc-300">
                    {r.role}
                  </span>
                </td>
                <td className="px-3 py-2 text-zinc-400 text-xs">
                  <span className="text-zinc-300">{r.entity_kind}</span>{" "}
                  <CopyId id={r.entity_id} />
                </td>
                <td className="px-3 py-2">
                  <CopyId id={r.user_id} />
                </td>
                <td className="px-3 py-2 text-zinc-400 text-xs">
                  {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                </td>
                <td className="px-3 py-2">
                  <button
                    onClick={() => revokeRole(r.id)}
                    disabled={revokeLoading === r.id}
                    className="rounded bg-red-800 px-2 py-0.5 text-xs text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    {revokeLoading === r.id ? "…" : "Revoke"}
                  </button>
                </td>
              </tr>
            ))}
            {roles.length === 0 && !loading && (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-4 text-center text-sm text-zinc-500"
                >
                  No roles found.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-4 text-center text-sm text-zinc-500"
                >
                  Loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
